/**
 * Mock Students PA APIs — Phase 1.
 * Replace via agentPaApi.js when live quotation endpoints are ready.
 */

import {
  buildRateCardPayload,
  getPaPremium,
  PA_OPTIONS,
} from '@/constants/paStudentsRateCard';

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const mockQuoteId = () => `MOCK-PA-${Date.now().toString(36).toUpperCase()}`;
const QUOTE_NOT_FOUND = 'Quote not found';

const quotesStore = new Map();
const STORAGE_KEY = 'pacis:pa:mock:quotes';

function loadStoreFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    Object.entries(obj).forEach(([k, v]) => quotesStore.set(k, v));
  } catch { /* ignore */ }
}

function persistStoreToSession() {
  const obj = {};
  quotesStore.forEach((v, k) => { obj[k] = v; });
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
}

loadStoreFromSession();

export async function mockGetPaRateCard() {
  await delay(400);
  return {
    success: true,
    data: buildRateCardPayload(),
    mock: true,
  };
}

export async function mockCreatePaQuotation(payload) {
  await delay(700);
  const quoteId = mockQuoteId();
  const premium = getPaPremium(payload.courseDuration, payload.optionCode);
  const option = PA_OPTIONS.find((o) => o.code === payload.optionCode);
  const record = {
    quoteId,
    ...payload,
    premium,
    optionName: option?.name || payload.optionCode,
    benefits: option?.benefits || [],
    status: 'DRAFT',
    createdAt: new Date().toISOString(),
    mock: true,
  };
  quotesStore.set(quoteId, record);
  persistStoreToSession();
  return { success: true, data: record, mock: true };
}

export async function mockUpdatePaQuotation(quoteId, payload) {
  await delay(500);
  const existing = quotesStore.get(quoteId) || { quoteId };
  const merged = { ...existing, ...payload, quoteId };
  if (payload.coverFrom) {
    merged.coverFrom = payload.coverFrom;
  }
  if (payload.optionCode && payload.courseDuration) {
    merged.premium = getPaPremium(payload.courseDuration, payload.optionCode);
    const option = PA_OPTIONS.find((o) => o.code === payload.optionCode);
    merged.optionName = option?.name;
    merged.benefits = option?.benefits || [];
  }
  quotesStore.set(quoteId, merged);
  persistStoreToSession();
  return { success: true, data: merged, mock: true };
}

export async function mockRecalculatePaPremium(optionCode, courseDuration) {
  await delay(300);
  const premium = getPaPremium(courseDuration, optionCode);
  return { success: true, data: { premium, optionCode, courseDuration }, mock: true };
}

export async function mockGetPaQuotation(quoteId) {
  await delay(300);
  const data = quotesStore.get(quoteId);
  if (!data) {
    return { success: false, message: QUOTE_NOT_FOUND };
  }
  return { success: true, data, mock: true };
}

export async function mockIssuePaPolicy(quoteId) {
  await delay(1200);
  const q = quotesStore.get(quoteId);
  if (!q) return { success: false, message: QUOTE_NOT_FOUND };
  const policyNo = `PA/${new Date().getFullYear()}/${String(Math.floor(Math.random() * 900000) + 100000)}`;
  const updated = {
    ...q,
    status: 'POLICY_ISSUED',
    policyNo,
    issuedAt: new Date().toISOString(),
  };
  quotesStore.set(quoteId, updated);
  persistStoreToSession();
  return {
    success: true,
    data: {
      quoteId,
      policyNo,
      premium: q.premium,
      status: 'POLICY_ISSUED',
      proposerName: q.proposerName,
      mock: true,
    },
    mock: true,
  };
}

export async function mockPaPayment(quoteId, paymentMethod) {
  await delay(1500);
  const q = quotesStore.get(quoteId);
  if (!q) return { success: false, message: QUOTE_NOT_FOUND };
  const transactionRef = `MOCK-MPESA-${Date.now().toString(36).toUpperCase()}`;
  const updated = {
    ...q,
    paymentMethod: paymentMethod || 'mpesa',
    transactionRef,
    paymentStatus: 'PAID',
    status: 'PAYMENT_RECEIVED',
  };
  quotesStore.set(quoteId, updated);
  persistStoreToSession();
  return {
    success: true,
    data: { quoteId, transactionRef, paymentMethod: updated.paymentMethod },
    mock: true,
  };
}

export function isAgentPaMockEnabled() {
  const flag = process.env.VUE_APP_MOCK_AGENT_PA;
  // Mocks are opt-in: live by default, only mock when explicitly enabled.
  return flag === 'true' || flag === '1';
}
